// CourseDetailsPage.tsx
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Title, Text, Card, Button, Badge,
  Group, Stack, Skeleton, Box, Image, ActionIcon, Tooltip, Menu, Loader, Center,
} from '@mantine/core';
import {
  IconArrowLeft, IconCalendar, IconClock,
  IconFolder, IconPencil, IconRefresh, IconUser, IconForms,
  IconChevronDown, IconAlertTriangle,
} from '@tabler/icons-react';
import {
  useACCourseService, ACCourse, ACCourseModelView,
  useACCourseStatusService, ACCourseStatus,
} from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import Tabs from '@/components/ui/navigation/Tabs';

// ─── Tab components — each lives in its own file ───────────────────────────
import SessionTab      from './Tabs/SessionTab';
import DateTab         from './Tabs/DateTab';
import FacilitatorTab  from './Tabs/FacilitatorTab';
import DocumentTab     from './Tabs/DocumentTab';
import FormTab from './Tabs/FormTab';
import DangerZoneTab from './Tabs/DangerZoneTab';
import EditCourseDrawer from '../Forms/EditCourseDrawer';
import { tabStyle } from '@/components/ui/TabStyle';

export default function CourseDetailsPage() {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const courseService = useACCourseService();
  const courseStatusService = useACCourseStatusService();

  // ─── Page state ────────────────────────────────────────────────────────────
  const [course, setCourse]     = useState<ACCourse | null>(null);
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('session');
  const [imageLoaded, setImageLoaded] = useState(false);
  /** Edit lives in a drawer off the header button, matching the leader/facilitator detail pages. */
  const [editOpen, setEditOpen] = useState(false);

  // ─── Statuses for badges ─────────────────────────────────────────────────
    const [statuses, setStatuses]               = useState<ACCourseStatus[]>([]);
    const [loadingStatuses, setLoadingStatuses] = useState(false);
    const [changingStatus, setChangingStatus]   = useState(false);

  // ─── Fetch course ──────────────────────────────────────────────────────────
  const fetchCourse = useCallback(async (showLoading = true) => {
    if (!id) { setLoading(false); return; }
    if (showLoading) setLoading(true);
    try {
      const result = await courseService.getById(id);
      setCourse(result);
    } catch (e) {
      console.error(e);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [id, courseService]);

  useEffect(() => { fetchCourse(); }, [fetchCourse]);

  // Reset the image loading state whenever the underlying image changes
  useEffect(() => { setImageLoaded(false); }, [course?.imageURL]);

  // ─── Fetch statuses ───────────────────────────────────────────────────────
  const fetchStatuses = useCallback(async () => {
  setLoadingStatuses(true);
  try {
    setStatuses(await courseStatusService.getAll());
  } catch (e) {
    console.error('Error fetching course statuses:', e);
    toast.error('Error', 'Could not load course statuses.');
  } finally {
    setLoadingStatuses(false);
  }
}, [courseStatusService]);

useEffect(() => { fetchStatuses(); }, [fetchStatuses]);

const handleStatusChange = async (newStatusID: number) => {
  if (!course || newStatusID === course.statusID) return;

  setChangingStatus(true);
  try {
    await courseService.changeStatus(courseId, newStatusID);
    toast.success('Status Updated', 'Course status changed.');
    await fetchCourse();
  } catch (e) {
    console.error('Error changing course status:', e);
    toast.error('Error', 'Failed to change status.');
  } finally {
    setChangingStatus(false);
  }
};

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchCourse(false);
    } finally {
      setRefreshing(false);
    }
  }, [fetchCourse]);

  // ─── Derived values ────────────────────────────────────────────────────────
  // isActive: prefer the boolean flag; fall back to statusID === 1 if absent
  const isActive  = course?.isActive ?? (course?.statusID === 1);
  // courseId as a number for tab props (tabs use numeric ID in SDK calls)
  const courseId  = Number(id);

  if (!id) return null;

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Container size="xl" py="xl">

      {/* Back button */}
      <Button
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="md"
        onClick={() => navigate(-1)}
      >
        Back
      </Button>

      {/* ── Page header: Title + Refresh + Edit ── */}
      <Group justify="space-between" align="center" mb="md">
        <Title order={2}>Course Details</Title>
        <Group gap="xs" align="center">
          <Tooltip label="Refresh" withArrow>
            <ActionIcon
              variant="subtle"
              onClick={handleRefresh}
              loading={refreshing}
              size="lg"
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>

          <Button
            leftSection={<IconPencil size={16} />}
            onClick={() => setEditOpen(true)}
            disabled={!course}
          >
            Edit Course
          </Button>

          <Menu shadow="md" width={220} position="bottom-end">
            <Menu.Target>
              <Button
                variant="default"
                rightSection={changingStatus ? <Loader size={14} /> : <IconChevronDown size={14} />}
                disabled={!course || loadingStatuses || changingStatus}
              >
                {course?.statusID
                  ? statuses.find((s) => s.statusID === course.statusID)?.name ?? 'Change status'
                  : 'Change status'}
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>Change status</Menu.Label>

              {loadingStatuses ? (
                <Center py="sm">
                  <Loader size="xs" />
                </Center>
              ) : (
                statuses.map((s) => (
                  <Menu.Item
                    key={s.statusID}
                    onClick={() => handleStatusChange(s.statusID)}
                    disabled={s.statusID === course?.statusID}
                    leftSection={
                      s.color ? (
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: s.color,
                            flexShrink: 0,
                          }}
                        />
                      ) : null
                    }
                  >
                    {s.name ?? `Status ${s.statusID}`}
                  </Menu.Item>
                ))
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

     
      {/* ── Profile card ───────────────────────────────────────────────────── */}
      <Card withBorder radius="md" padding="lg" mb="md">
        {loading ? (
          <Group gap="md" align="flex-start">
            <Skeleton radius="md" height={64} width={64} />
            <Stack gap={8} style={{ flex: 1 }}>
              <Skeleton height={28} width="35%" />
              <Skeleton height={14} width="70%" />
              <Skeleton height={14} width="50%" />
            </Stack>
          </Group>
        ) : (
          <Group justify="space-between" align="flex-start">
            <Group gap="md" align="flex-start">

              {/* Color box with course icon */}
              <Box pos="relative" h={75} w={75} style={{ flexShrink: 0 }}>
                {!imageLoaded && (
                  <Skeleton radius="md" height={75} width={75} pos="absolute" top={0} left={0} />
                )}
                <Image
                  src={course?.imageURL || '/Course-placeholder.png'}
                  alt="Course image"
                  radius="md"
                  h={75}
                  w={75}
                  fit="cover"
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageLoaded(true)}
                  style={{ opacity: imageLoaded ? 1 : 0, transition: 'opacity 150ms ease' }}
                />
              </Box>

              <Stack gap={4}>
                <Group gap="xs" align="center">
                  <Title order={3}>{course?.name ?? 'Unknown Course'}</Title>
                  {(() => {
                    const currentStatus = statuses.find((s) => s.statusID === course?.statusID);
                    return (
                      <Badge
                        variant="light"
                        style={
                          currentStatus?.color
                            ? { backgroundColor: `${currentStatus.color}22`, color: currentStatus.color }
                            : undefined
                        }
                        color={currentStatus?.color ? undefined : (isActive ? 'green' : 'red')}
                      >
                        {currentStatus?.name ?? (isActive ? 'Active' : 'Inactive')}
                      </Badge>
                    );
                  })()}
                </Group>
                {course?.description && (
                  <Text size="sm" c="dimmed" style={{ maxWidth: 600 }}>
                    {course.description}
                  </Text>
                )}
              </Stack>
            </Group>

            {/* Active / Inactive badge — top right */}
            <Badge size="lg" color={isActive ? 'green' : 'red'} variant="filled">
              {isActive ? 'Active' : 'Inactive'}
            </Badge>
          </Group>
        )}
      </Card>

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <Tabs value={activeTab} onChange={setActiveTab}>
       <Tabs.List>
        <Tabs.Tab value="session"      leftSection={<IconClock size={16} />} style={tabStyle(activeTab === 'session')}>Sessions</Tabs.Tab>
        <Tabs.Tab value="form" leftSection={<IconForms size={16} />} style={tabStyle(activeTab === 'form')}>Custom Form</Tabs.Tab>
        <Tabs.Tab value="date"         leftSection={<IconCalendar size={16} />} style={tabStyle(activeTab === 'date')}>Dates</Tabs.Tab>
        <Tabs.Tab value="facilitators" leftSection={<IconUser size={16} />} style={tabStyle(activeTab === 'facilitators')}>Facilitators</Tabs.Tab>
        <Tabs.Tab value="files"        leftSection={<IconFolder size={16} />} style={tabStyle(activeTab === 'files')}> Course Materials</Tabs.Tab>
        <Tabs.Tab value="danger"       leftSection={<IconAlertTriangle size={16} />} style={tabStyle(activeTab === 'danger')} color="red">Danger Zone</Tabs.Tab>
      </Tabs.List>

        {/* All tab panels share a single bordered card */}
        <Card withBorder radius="md" padding="lg" mt="sm">

          {/* ── Sessions tab ──────────────────────────────────────────────── */}
          <Tabs.Panel value="session" pt="md">
            <SessionTab
              courseId={courseId}
              active={activeTab === 'session'}
              onCourseChange={fetchCourse}
            />
          </Tabs.Panel>
          
          {/* ── Custom Form tab ──────────────────────────────────────────────── */}
          <Tabs.Panel value="form" pt="md">
            <FormTab
              courseId={courseId}
              active={activeTab === 'form'}
              onCourseChange={fetchCourse}
            />
          </Tabs.Panel>

          {/* ── Dates tab ─────────────────────────────────────────────────── */}
          <Tabs.Panel value="date" pt="md">
            <DateTab
              courseId={courseId}
              active={activeTab === 'date'}
              onCourseChange={fetchCourse}
            />
          </Tabs.Panel>

          {/* ── Facilitators tab ──────────────────────────────────────────── */}
          <Tabs.Panel value="facilitators" pt="md">
            <FacilitatorTab
              courseId={courseId}
              active={activeTab === 'facilitators'}
              onCourseChange={fetchCourse}
            />
          </Tabs.Panel>

          {/* ── Files tab ─────────────────────────────────────────────────── */}
          <Tabs.Panel value="files" pt="md">
            <DocumentTab
              courseId={courseId}
              active={activeTab === 'files'}
              onCourseChange={fetchCourse}
            />
          </Tabs.Panel>

          {/* ── Danger Zone tab ───────────────────────────────────────────── */}
          <Tabs.Panel value="danger" pt="md">
            <DangerZoneTab
              course={course}
              onDeleted={() => navigate('/Grow/Courses')}
            />
          </Tabs.Panel>

        </Card>
      </Tabs>

      {/* ── Edit drawer ── */}
      <EditCourseDrawer
        opened={editOpen}
        course={course}
        onClose={() => setEditOpen(false)}
        onUpdated={() => { fetchCourse(); setEditOpen(false); }}
      />
    </Container>
  );
}